import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { enableObservabilityAiTestError, vegaTermsAcceptanceMode } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackModal } from '@gonfalon/feedback/ContextualFeedbackModal';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toDiagnosticUsage, toHref, toSecurity, toVegaSettings } from '@gonfalon/navigator/navigators';
import { useShareConversationMutation } from '@gonfalon/observability-graphql/hooks';
import { AiAccessModal, hasVegaAiAccess } from '@gonfalon/observability-ui/components/AiAccessModal';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Button } from '@gonfalon/observability-ui/components/Button';
import { Dialog } from '@gonfalon/observability-ui/components/Dialog';
import { IconSolidTraces, IconSolidX } from '@gonfalon/observability-ui/components/icons';
import { AI_BOT_NAME } from '@gonfalon/observability-ui/constants';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import {
  Alert,
  DialogTrigger,
  Focusable,
  Heading,
  IconButton,
  Button as LaunchpadButton,
  Dialog as LaunchpadDialog,
  Menu as LDMenu,
  MenuItem,
  MenuTrigger,
  Popover,
  Text,
  toastQueue,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useAgentInsights } from '../../AgentInsightsContext';
import { CONVERSATION_TITLE_MAX, NEW_CHAT_PLACEHOLDER, resolveConversationTitle } from '../../conversationTitle';
import { useAiChatLimit } from '../../hooks/useAiChatLimit';
import { useRetrieveConversation } from '../../hooks/useRetrieveConversation';
import {
  useVegaAccessDenied,
  VegaPermissionDeniedAlert,
} from '../../internal/components/AgentInsights/VegaPermissionDenied';
import {
  shouldShowVegaO11yConversationLink,
  useOpenVegaO11yConversation,
  VIEW_TRACES_LABEL,
  VIEW_TRACES_MENU_ID,
} from '../../internal/components/VegaO11yConversationLink';
import { writeTextToClipboard } from '../../internal/copyTextToClipboard';
import {
  trackVegaAiConversationCleared,
  trackVegaAiConversationLoaded,
  trackVegaAiConversationShared,
  trackVegaAiPanelClosed,
  trackVegaAiPanelOpened,
} from '../../vegaAnalytics';

import type { AgentPromptMutationFn } from './AgentInsights';
import { ConversationHistoryView } from './ConversationHistoryView';
import { VegaConversation } from './VegaConversation';

import * as styles from '../../internal/components/AgentInsights/AgentInsightsPanel.css';

// Stable empty list — inline `[]` would invalidate ArtifactLookupProvider's useMemo each render.
export interface AgentInsightsPanelProps {
  nonGenAiSkill?: boolean;
  agentPromptMutation: AgentPromptMutationFn;
  sending: boolean;
  enableAgentResponseSelector?: boolean;
}

export const AgentInsightsPanel: React.FC<AgentInsightsPanelProps> = ({
  nonGenAiSkill = false,
  agentPromptMutation,
  sending,
  enableAgentResponseSelector = false,
}) => {
  const {
    isOpen,
    close,
    currentConversationId,
    conversationIdToLoad,
    open: openPanel,
    panelWidth,
    setPanelWidth,
    maxPanelWidth,
    setIsDraggingPanel,
    setLivePanelWidth,
    savedConversationTitle,
    firstUserMessagePreview,
    adoptConversationChrome,
    historyView,
    openHistory,
    closeHistory,
  } = useAgentInsights();
  // conversation_history has no title. Same-thread reloads keep the model
  // title already in chrome; switching threads must not pass the old name.
  const savedConversationTitleRef = useLatestRef(savedConversationTitle);
  const currentConversationIdRef = useLatestRef(currentConversationId);
  const titleForHistoryReload = useCallback(
    (conversationId: string) => {
      return conversationId === currentConversationIdRef.current ? savedConversationTitleRef.current : undefined;
    },
    [currentConversationIdRef, savedConversationTitleRef],
  );
  const { account, profile } = useAccountContext();
  const navigate = useNavigate();
  const { projectId, projectKey } = useProjectId();
  const accessDenied = useVegaAccessDenied();
  const openO11yConversation = useOpenVegaO11yConversation();
  const showViewTraces = shouldShowVegaO11yConversationLink(currentConversationId);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const clearRef = useRef<(() => void) | null>(null);
  const focusRef = useRef<(() => void) | null>(null);
  const clearPromptRef = useRef<(() => void) | null>(null);
  const enableAiTestError = enableObservabilityAiTestError();
  const [shouldThrowError, setShouldThrowError] = useState(false);
  const [showAiRedirectModal, setShowAiRedirectModal] = useState(false);
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [shareConversation] = useShareConversationMutation();
  const { retrieveConversation, isLoadingConversation, conversationNotShared, resetConversationNotShared } =
    useRetrieveConversation();

  const goToSettings = useCallback(() => {
    close();
    navigate(toHref(toVegaSettings({ projectKey })));
  }, [close, navigate, projectKey]);

  const clearUrlParameter = useCallback((paramName: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(paramName);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const setUrlParameter = useCallback((paramName: string, paramValue: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(paramName, paramValue);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const { data: aiChatLimitData } = useAiChatLimit();

  const handleOpenHistoryConversation = useCallback(
    (conversationId: string, title?: string | null) => {
      adoptConversationChrome(conversationId, title);
      closeHistory();
      retrieveConversation(conversationId, title)
        .then(() => {
          trackVegaAiConversationLoaded(conversationId);
        })
        .catch((error: unknown) => {
          console.error('Failed to load conversation from history:', error);
        });
    },
    [adoptConversationChrome, closeHistory, retrieveConversation],
  );

  const handleNewChat = useCallback(() => {
    trackVegaAiConversationCleared({
      conversationId: currentConversationId || undefined,
    });
    resetConversationNotShared();
    closeHistory();
    clearRef.current?.();
    clearPromptRef.current?.();
  }, [closeHistory, currentConversationId, resetConversationNotShared]);

  const handleShare = useCallback(async () => {
    if (!currentConversationId) {
      return;
    }
    try {
      // Attempt to mark the conversation as shared. This will succeed for
      // conversations owned by the current user and gracefully fail for
      // conversations owned by others (which are already shared).
      await shareConversation({
        variables: {
          project_id: projectId,
          conversation_id: currentConversationId,
        },
      });
    } catch {
      // Ignore share mutation errors — the conversation may already be
      // shared or owned by another user. We still want to copy the link.
    }
    trackVegaAiConversationShared(currentConversationId);
    if (await writeTextToClipboard(window.location.href)) {
      toastQueue.add({ status: 'info', title: msg('vega.panel.linkCopied') });
    } else {
      toastQueue.add({ status: 'error', title: msg('vega.panel.copyLinkFailed') });
    }
  }, [currentConversationId, shareConversation, projectId]);

  // Throw error during render phase so error boundary can catch it
  if (shouldThrowError) {
    throw new Error('A very bad thing happened! (just kidding this is a test error)');
  }

  const dialogStore = Dialog.useStore({
    open: isOpen && (nonGenAiSkill || hasVegaAiAccess(account)),
    setOpen: (open) => {
      if (!open) {
        trackVegaAiPanelClosed({
          conversationId: currentConversationId || undefined,
        });
        close();
      }
    },
  });

  // Show modal instead of panel if AI is not enabled (skipped when the
  // consumer opts out of the gen-AI gate, e.g. account-scoped skill panels).
  useEffect(() => {
    if (nonGenAiSkill) {
      return;
    }
    if (isOpen && !hasVegaAiAccess(account)) {
      setShowAiRedirectModal(true);
      close(); // Close the panel request
    }
  }, [isOpen, account.isGeneralGenAiEnabled, close, nonGenAiSkill]);

  // Track panel opened when isOpen changes to true
  useEffect(() => {
    if (isOpen) {
      trackVegaAiPanelOpened({
        conversationId: currentConversationId || undefined,
      });
    }
  }, [isOpen]); // Only depend on isOpen, not currentConversationId

  const updateWidth = useCallback(
    (newWidth: number) => {
      const clampedWidth = Math.min(Math.max(newWidth, 20), maxPanelWidth);

      if (dialogRef.current) {
        dialogRef.current.style.setProperty('--panel-width', `${clampedWidth}%`);
      }

      return clampedWidth;
    },
    [maxPanelWidth],
  );

  const dragWidthRef = useRef(panelWidth);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }

      e.preventDefault();

      const newWidth = 100 - (e.clientX / window.innerWidth) * 100;
      dragWidthRef.current = updateWidth(newWidth);
      setLivePanelWidth(dragWidthRef.current);
    },
    [updateWidth, setLivePanelWidth],
  );

  const mouseUpImplRef = useRef<() => void>(() => {});
  const stableMouseUp = useCallback(() => {
    mouseUpImplRef.current();
  }, []);
  const handleMouseUp = useCallback(() => {
    dragging.current = false;
    setIsDraggingPanel(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stableMouseUp);
    setPanelWidth(dragWidthRef.current);
    setLivePanelWidth(dragWidthRef.current);
  }, [handleMouseMove, setPanelWidth, setIsDraggingPanel, setLivePanelWidth, stableMouseUp]);
  useLayoutEffect(() => {
    mouseUpImplRef.current = handleMouseUp;
  }, [handleMouseUp]);

  const handleMouseDown = useCallback(() => {
    dragging.current = true;
    setIsDraggingPanel(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stableMouseUp);
  }, [handleMouseMove, stableMouseUp, setIsDraggingPanel]);

  useEffect(() => {
    const dragHandle = dragHandleRef.current;
    if (dragHandle) {
      dragHandle.addEventListener('mousedown', handleMouseDown);
      return () => {
        dragHandle.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [handleMouseDown]);

  // Initialize panel width from persisted value, clamped to max
  useEffect(() => {
    if (isOpen) {
      const clamped = updateWidth(panelWidth);
      setLivePanelWidth(clamped);
      if (clamped !== panelWidth) {
        setPanelWidth(clamped);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only run on open/max change, not when panelWidth updates
  }, [isOpen, updateWidth]);

  // Check for AgentConversationId URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const agentConversationId = urlParams.get('AgentConversationId');
    const showVega = urlParams.get('showVega');
    const configureAgent = urlParams.get('ConfigureAgent');

    // Old Slack alert-investigation messages embed `?ConfigureAgent=true`
    // buttons that previously opened a settings view inline. The settings UI
    // now lives at /projects/:projectKey/vega/settings, so redirect those
    // buttons rather than dropping users into an empty Vega panel.
    if (configureAgent === 'true' && projectKey) {
      navigate(toVegaSettings({ projectKey }), { replace: true });
      return;
    }

    if (agentConversationId) {
      openPanel({
        context: {
          resourceOrQuery: [],
        },
      });

      // Load the conversation (user can continue it)
      retrieveConversation(agentConversationId, titleForHistoryReload(agentConversationId))
        .then(() => {
          trackVegaAiConversationLoaded(agentConversationId);
        })
        .catch((error: unknown) => {
          console.error('Failed to load conversation from URL parameter:', error);
        });
    } else if (showVega === '1') {
      openPanel({
        context: {
          resourceOrQuery: [],
        },
      });
    }
  }, [retrieveConversation, openPanel, navigate, projectKey, titleForHistoryReload]);

  // Load conversation when conversationIdToLoad is provided (for button clicks)
  useEffect(() => {
    if (conversationIdToLoad) {
      retrieveConversation(conversationIdToLoad, titleForHistoryReload(conversationIdToLoad))
        .then(() => {
          trackVegaAiConversationLoaded(conversationIdToLoad);
        })
        .catch((error: unknown) => {
          console.error('Failed to load conversation:', error);
        });
    }
  }, [conversationIdToLoad, retrieveConversation, titleForHistoryReload]);

  // Automatically sync URL parameter with panel state and conversation ID
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlConversationId = urlParams.get('AgentConversationId');

    // Add parameter when panel is open and has a conversation ID
    if (isOpen && currentConversationId) {
      // Only update if different to avoid unnecessary history updates
      if (urlConversationId !== currentConversationId) {
        setUrlParameter('AgentConversationId', currentConversationId);
      }
    }
    // Remove parameter when panel is closed or there's no active conversation
    else if (urlConversationId && (!isOpen || !currentConversationId)) {
      clearUrlParameter('AgentConversationId');
    }

    if (isOpen) {
      setUrlParameter('showVega', '1');
    } else {
      clearUrlParameter('showVega');
    }
  }, [isOpen, currentConversationId, setUrlParameter, clearUrlParameter]);

  // Auto-focus textarea when panel opens
  useEffect(() => {
    if (isOpen && focusRef.current) {
      setTimeout(() => {
        focusRef.current?.();
      }, 100);
    }
  }, [isOpen]);

  const conversationTitle = resolveConversationTitle(
    {
      title: savedConversationTitle,
      message_preview: firstUserMessagePreview,
    },
    { placeholder: NEW_CHAT_PLACEHOLDER, maxLength: CONVERSATION_TITLE_MAX },
  );

  const renderPanelHeader = () => {
    if (historyView) {
      return (
        <>
          <Box display="flex" alignItems="center" py="8" gap="8" minWidth="none">
            <Icon name="clock" size="small" className={styles.sparklesIcon} />
            <span className={styles.panelTitle}>
              <Message id="vega.panel.historyTitle" values={{ botName: AI_BOT_NAME }} />
            </span>
          </Box>
          <Box display="flex" alignItems="center" gap="8">
            <TooltipTrigger>
              <Button
                kind="secondary"
                emphasis="low"
                size="small"
                aria-label={msg('vega.panel.closeHistory')}
                onClick={closeHistory}
                className={styles.iconButton}
              >
                <IconSolidX />
              </Button>
              <Tooltip placement="bottom">
                <Message id="vega.panel.closeHistory" />
              </Tooltip>
            </TooltipTrigger>
          </Box>
        </>
      );
    }

    const shareButton = (
      <LaunchpadButton
        variant="default"
        size="small"
        aria-label={msg('vega.panel.share')}
        onPress={handleShare}
        isDisabled={!currentConversationId}
        className={styles.iconButton}
      >
        <Icon name="link" size="small" />
      </LaunchpadButton>
    );

    return (
      <>
        <Box display="flex" alignItems="center" py="8" gap="8" minWidth="none" cssClass={styles.panelTitleWrap}>
          <span className={styles.panelTitle} title={conversationTitle}>
            {conversationTitle}
          </span>
        </Box>
        <Box display="flex" alignItems="center" gap="8" flexShrink={0}>
          <DialogTrigger>
            <IconButton
              variant="minimal"
              size="small"
              icon="info-circle-outline"
              aria-label={msg('vega.panel.infoLabel')}
              className={styles.iconButton}
            />
            <Popover>
              <LaunchpadDialog className={styles.infoDialog}>
                <Text slot="subtitle">
                  <Message
                    id="vega.panel.aboutBody"
                    values={{
                      botName: AI_BOT_NAME,
                      link: (chunks) => (
                        <a
                          href={toHref(toSecurity({ hash: 'enable-genai' }))}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.inlineLink}
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </Text>
              </LaunchpadDialog>
            </Popover>
          </DialogTrigger>

          <MenuTrigger>
            <LaunchpadButton
              variant="minimal"
              aria-label={msg('vega.panel.moreOptionsLabel')}
              className={styles.iconButton}
            >
              <Icon name="more-vert" size="small" />
            </LaunchpadButton>
            <Popover placement="bottom end">
              <LDMenu
                onAction={(action) => {
                  switch (action) {
                    case 'new-chat':
                      handleNewChat();
                      break;
                    case 'send-feedback':
                      setShowingFeedback(true);
                      break;
                    case 'vega-settings':
                      goToSettings();
                      break;
                    case VIEW_TRACES_MENU_ID:
                      if (currentConversationId) {
                        openO11yConversation(currentConversationId);
                      }
                      break;
                    case 'test-error':
                      setShouldThrowError(true);
                      break;
                    default:
                      break;
                  }
                }}
              >
                <MenuItem id="new-chat">
                  <Text slot="label">
                    <Icon name="add" size="small" /> <Message id="vega.panel.newChat" />
                  </Text>
                </MenuItem>
                {showViewTraces && (
                  <MenuItem id={VIEW_TRACES_MENU_ID}>
                    <Text slot="label">
                      <IconSolidTraces size={14} /> {VIEW_TRACES_LABEL}
                    </Text>
                  </MenuItem>
                )}
                <MenuItem id="send-feedback">
                  <Text slot="label">
                    <Icon name="chat-bubble-outline" size="small" /> <Message id="vega.panel.sendFeedback" />
                  </Text>
                </MenuItem>
                <MenuItem id="vega-settings">
                  <Text slot="label">
                    <Icon name="gear" size="small" /> {AI_BOT_NAME} settings
                  </Text>
                </MenuItem>
                {enableAiTestError && (
                  <MenuItem id="test-error">
                    <Text slot="label">
                      <Icon name="warning" size="small" /> <Message id="vega.panel.testError" />
                    </Text>
                  </MenuItem>
                )}
              </LDMenu>
            </Popover>
          </MenuTrigger>

          <TooltipTrigger>
            {currentConversationId ? shareButton : <Focusable>{shareButton}</Focusable>}
            <Tooltip placement="bottom">
              <Message id="vega.panel.share" />
            </Tooltip>
          </TooltipTrigger>

          <TooltipTrigger>
            <LaunchpadButton
              variant="default"
              size="small"
              aria-label={msg('vega.panel.history')}
              onPress={() => openHistory()}
              className={styles.iconButton}
            >
              <Icon name="clock" size="small" />
            </LaunchpadButton>
            <Tooltip placement="bottom">
              <Message id="vega.panel.history" />
            </Tooltip>
          </TooltipTrigger>

          <Box borderLeft="dividerWeak" cssClass={styles.headerDivider} />

          <Button
            kind="secondary"
            emphasis="low"
            size="small"
            aria-label={msg('vega.panel.closeLabel')}
            onClick={close}
            className={styles.iconButton}
          >
            <IconSolidX />
          </Button>
        </Box>
      </>
    );
  };

  const renderPanelContent = () => {
    // Ahead of every other body state: someone without access shouldn't reach the history list or
    // the not-shared notice. VegaConversation gates independently for hosts outside this panel.
    if (accessDenied) {
      return <VegaPermissionDeniedAlert />;
    }

    if (historyView) {
      return (
        <ConversationHistoryView
          key={historyView.requestId}
          initialView={historyView}
          onOpenConversation={handleOpenHistoryConversation}
          currentUserId={profile._id}
          currentUserEmail={profile.email}
          aiChatLimitData={aiChatLimitData}
          usageHref={toDiagnosticUsage().pathname}
        />
      );
    }

    if (conversationNotShared) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" padding="16" cssClass={styles.centeredAlertBox}>
          <Alert status="warning">
            <Heading slot="title">
              <Message id="vega.panel.conversationNotAvailable" />
            </Heading>
            <Text>
              <Message id="vega.panel.notSharedBody" values={{ botName: AI_BOT_NAME }} />
            </Text>
          </Alert>
        </Box>
      );
    }

    return (
      <VegaConversation
        onClearRef={clearRef}
        onFocusRef={focusRef}
        onClearPromptRef={clearPromptRef}
        isLoadingConversation={isLoadingConversation}
        simplifiedFooter={nonGenAiSkill}
        agentPromptMutation={agentPromptMutation}
        sending={sending}
        enableAgentResponseSelector={enableAgentResponseSelector}
      />
    );
  };

  return createPortal(
    <>
      <Dialog
        store={dialogStore}
        modal={false}
        autoFocusOnShow={false}
        hideOnEscape
        hideOnInteractOutside
        backdrop={false}
        className={styles.panel}
        ref={dialogRef}
        id="agent-insights-panel"
      >
        <div ref={dragHandleRef} className={styles.panelDragHandle} />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          borderBottom="divider"
          cssClass={styles.panelHeader}
        >
          {renderPanelHeader()}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          overflow="hidden"
          minHeight="none"
          cssClass={styles.panelBody}
        >
          {renderPanelContent()}
        </Box>
      </Dialog>
      {!nonGenAiSkill && showAiRedirectModal && (
        <AiAccessModal
          mode={vegaTermsAcceptanceMode()}
          isOpen={showAiRedirectModal}
          onOpenChange={setShowAiRedirectModal}
          product={msg('vega.panel.aiAccessProduct')}
        />
      )}
      <ContextualFeedbackModal
        flagKey="feedback-recipient-ai-agent-conversation"
        isOpen={showingFeedback}
        onOpenChange={setShowingFeedback}
        className={styles.feedbackModal}
      />
    </>,
    document.body,
  );
};
